import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const DEFAULT_SIZES = [
  { label: "Small", price: 0 },
  { label: "Medium", price: 0 },
  { label: "Large", price: 0 },
];

// Standard rice / side choices offered on combo meals (customer picks one).
const RICE_PRESETS = ["White Rice", "Garlic Rice", "Half & Half (Rice + Noodle)"];

// Categories the editor sets automatically per sale type. A category NOT in this
// list is treated as a custom one and is preserved when the sale type changes.
const AUTO_CATEGORIES = ["A La Carte", "Combo Meals", "Party Trays", "Drinks"];
const AUTO_CATEGORY_BY_TYPE = {
  combo: "Combo Meals",
  party_tray: "Party Trays",
  drinks: "Drinks",
  ala_carte: "A La Carte",
};

// Sale types that carry a single flat price (no size grid).
const FLAT_PRICE_TYPES = ["combo", "drinks"];

const EMPTY = {
  name: "",
  category: "A La Carte",
  price: 0,
  sale_type: "ala_carte",
  description: "",
  image: "",
  available: true,
  featured: false,
  is_lechon: false,
  is_special: false,
  spicy_level: 0,
  prep_time_min: 15,
  daily_menu_eligible: true,
  party_tray_eligible: false,
  combo_eligible: false,
  combo_entree_count: 0,
  combo_choices: [],
  rice_options: ["White Rice", "Garlic Rice"],
  sizes: DEFAULT_SIZES,
  addons: [],
};

function normalizeItem(item) {
  const saleType =
    item.sale_type ||
    (item.combo_eligible ? "combo" : item.party_tray_eligible ? "party_tray" : "ala_carte");

  return {
    ...EMPTY,
    ...item,
    sale_type: saleType,
    combo_eligible: saleType === "combo",
    party_tray_eligible: saleType === "party_tray",
    combo_entree_count: item.combo_entree_count || (saleType === "combo" ? 1 : 0),
    combo_choices: item.combo_choices || [],
    rice_options: item.rice_options || ["White Rice", "Garlic Rice"],
    sizes: FLAT_PRICE_TYPES.includes(saleType) ? [] : (item.sizes?.length ? item.sizes : DEFAULT_SIZES),
  };
}

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/menu").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Combo entrees are real à-la-carte dishes only — never drinks, desserts, or water.
  const entreeOptions = useMemo(() => {
    return items
      .filter((m) => {
        const st = m.sale_type || (m.combo_eligible ? "combo" : m.party_tray_eligible ? "party_tray" : "ala_carte");
        if (st !== "ala_carte" || m.available === false) return false;
        const cat = (m.category || "").toLowerCase();
        return !/drink|dessert|beverage|water|soda|juice/.test(cat);
      })
      .map((m) => m.name)
      .sort();
  }, [items]);

  const setSaleType = (saleType) => {
    // Preserve a category the user typed themselves; only auto-fill the default
    // ones (so switching type doesn't wipe e.g. "Filipino Drinks").
    const keepCustomCategory = editing.category && !AUTO_CATEGORIES.includes(editing.category);
    setEditing({
      ...editing,
      sale_type: saleType,
      category: keepCustomCategory ? editing.category : (AUTO_CATEGORY_BY_TYPE[saleType] || "A La Carte"),
      combo_eligible: saleType === "combo",
      party_tray_eligible: saleType === "party_tray",
      combo_entree_count: saleType === "combo" ? Number(editing.combo_entree_count || 1) : 0,
      combo_choices: saleType === "combo" ? (editing.combo_choices || []) : [],
      sizes: FLAT_PRICE_TYPES.includes(saleType) ? [] : (editing.sizes?.length ? editing.sizes : DEFAULT_SIZES),
    });
  };

  const updateSize = (idx, field, value) => {
    const sizes = [...(editing.sizes || [])];
    sizes[idx] = { ...sizes[idx], [field]: field === "price" ? Number(value || 0) : value };
    setEditing({ ...editing, sizes });
  };

  const addSize = () => setEditing({ ...editing, sizes: [...(editing.sizes || []), { label: "New Size", price: 0 }] });
  const removeSize = (idx) => setEditing({ ...editing, sizes: (editing.sizes || []).filter((_, i) => i !== idx) });

  const toggleComboChoice = (name) => {
    const current = editing.combo_choices || [];
    setEditing({
      ...editing,
      combo_choices: current.includes(name) ? current.filter((x) => x !== name) : [...current, name],
    });
  };

  const toggleRiceOption = (name) => {
    const current = editing.rice_options || [];
    setEditing({
      ...editing,
      rice_options: current.includes(name) ? current.filter((x) => x !== name) : [...current, name],
    });
  };

  const save = async () => {
    try {
      const payload = normalizeItem(editing);
      payload.price = Number(payload.price || 0);
      payload.sizes = FLAT_PRICE_TYPES.includes(payload.sale_type) ? [] : (payload.sizes || []).filter((s) => s.label && Number(s.price) >= 0);
      payload.combo_choices = payload.sale_type === "combo" ? (payload.combo_choices || []).filter(Boolean) : [];
      payload.rice_options = payload.sale_type === "combo" ? (payload.rice_options || []).filter(Boolean) : [];
      payload.combo_entree_count = payload.sale_type === "combo" ? Number(payload.combo_entree_count || 1) : 0;
      payload.combo_eligible = payload.sale_type === "combo";
      payload.party_tray_eligible = payload.sale_type === "party_tray";

      if ((payload.sale_type === "ala_carte" || payload.sale_type === "party_tray") && payload.sizes.length > 0) {
        payload.price = Number(payload.sizes[0].price || payload.price || 0);
      }

      if (payload.id) await api.put(`/menu/${payload.id}`, payload);
      else await api.post("/menu", payload);

      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/menu/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <div data-testid="admin-menu">
      <div className="flex justify-between mb-5">
        <h1 className="font-heading text-3xl text-[#FAF9F6]">Restaurant Menu</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="menu-new" className="btn-chow">
          <Plus className="w-4 h-4 inline" /> New
        </button>
      </div>

      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#D4AF37]/80 text-xs uppercase border-b border-[#D4AF37]/15">
              <th className="p-3">Item</th>
              <th>Sale Type</th>
              <th>Category</th>
              <th>Price / Sizes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((raw) => {
              const m = normalizeItem(raw);
              return (
                <tr key={m.id} className="border-b border-white/5">
                  <td className="p-3 font-heading text-[#FAF9F6]">{m.name}</td>
                  <td className="text-xs uppercase">{m.sale_type.replace("_", " ")}</td>
                  <td>{m.category}</td>
                  <td className="text-[#D4AF37]">
                    {m.sale_type !== "combo" && m.sizes?.length
                      ? m.sizes.map((s) => `${s.label}: $${Number(s.price || 0).toFixed(2)}`).join(" / ")
                      : `$${Number(m.price || 0).toFixed(2)}`}
                  </td>
                  <td>
                    <button onClick={() => setEditing(normalizeItem(m))} className="text-[#D4AF37] mr-2">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(m.id)} className="text-[#E8A87C]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="admin-card p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-2xl text-[#D4AF37] mb-3">{editing.id ? "Edit" : "New"} menu item</h3>

            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs text-[#FAF9F6]/50 mb-1">How is this item sold?</label>
                <select
                  value={editing.sale_type}
                  onChange={(e) => setSaleType(e.target.value)}
                  className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                >
                  <option value="combo">Combo Meal — fixed price, select included entrees</option>
                  <option value="ala_carte">A La Carte — size-based price</option>
                  <option value="party_tray">Party Tray — size-based price</option>
                  <option value="drinks">Drinks & Dessert — fixed price</option>
                </select>
              </div>

              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
              <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Description" rows={3} className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />

              <div className="grid grid-cols-2 gap-2">
                <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Category" className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
                <input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value || 0) })} placeholder={editing.sale_type === "combo" ? "Fixed Combo Price" : "Base Price"} className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
              </div>

              <ImageUpload value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} recommendedSize="800 × 800" aspect="aspect-square" testId="menu-image" />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[#FAF9F6]">
                <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} /> Available</label>
                <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
                <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={editing.is_lechon} onChange={(e) => setEditing({ ...editing, is_lechon: e.target.checked })} /> Is Lechon</label>
                <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={editing.is_special} onChange={(e) => setEditing({ ...editing, is_special: e.target.checked })} /> Special</label>
              </div>

              {editing.sale_type === "combo" && (
                <div className="border border-[#D4AF37]/20 rounded p-4 space-y-3">
                  <h4 className="font-heading text-lg text-[#D4AF37]">Combo Meal Setup</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#FAF9F6]/50 mb-1">Combo Type / Required Entrees</label>
                      <select
                        value={editing.combo_entree_count}
                        onChange={(e) => setEditing({ ...editing, combo_entree_count: Number(e.target.value) })}
                        className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
                      >
                        <option value={1}>1 Entree Combo</option>
                        <option value={2}>2 Entree Combo</option>
                        <option value={3}>3 Entree Combo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#FAF9F6]/50 mb-1">Rice / Side Options (customer picks one)</label>
                      <div className="grid grid-cols-1 gap-1">
                        {[...new Set([...RICE_PRESETS, ...(editing.rice_options || [])])].map((r) => (
                          <label key={r} className="flex gap-2 items-center text-xs text-[#FAF9F6]/80 bg-[#0B0E14] p-2 rounded">
                            <input type="checkbox" checked={(editing.rice_options || []).includes(r)} onChange={() => toggleRiceOption(r)} />
                            {r}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-[#FAF9F6]/50 mb-2">Selectable Entrees</div>
                    <div className="grid md:grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                      {entreeOptions.map((name) => (
                        <label key={name} className="flex gap-2 items-center text-xs text-[#FAF9F6]/80 bg-[#0B0E14] p-2 rounded">
                          <input type="checkbox" checked={(editing.combo_choices || []).includes(name)} onChange={() => toggleComboChoice(name)} />
                          {name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {(editing.sale_type === "ala_carte" || editing.sale_type === "party_tray") && (
                <div className="border border-[#D4AF37]/20 rounded p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-heading text-lg text-[#D4AF37]">
                        {editing.sale_type === "party_tray" ? "Party Tray Sizes" : "A La Carte Sizes"}
                      </h4>
                      <p className="text-xs text-[#FAF9F6]/50">Small, Medium, Large or custom sizes.</p>
                    </div>
                    <button type="button" onClick={addSize} className="btn-chow-outline text-xs px-3 py-2">Add Size</button>
                  </div>

                  {(editing.sizes || []).map((s, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <input value={s.label} onChange={(e) => updateSize(idx, "label", e.target.value)} placeholder="Size label" className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
                      <input type="number" step="0.01" value={s.price} onChange={(e) => updateSize(idx, "price", e.target.value)} placeholder="Price" className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
                      <button type="button" onClick={() => removeSize(idx)} className="text-[#E8A87C] px-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="menu-save" className="btn-chow flex-1">Save</button>
              <button onClick={() => setEditing(null)} className="btn-chow-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
